import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Star, MessageCircle, Heart } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

function timeAgo(iso) {
    const d = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - d) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

const ICONS = { rating: Star, comment: MessageCircle, tip: Heart };
const BG = { rating: "bg-[#FFE55C]", comment: "bg-[#B8E1FF]", tip: "bg-[#FFB8D1]" };

function renderText(n) {
    const p = n.payload || {};
    if (n.kind === "rating") return <>@{p.from_username} rated your post <b>{p.rating}/10</b></>;
    if (n.kind === "comment") return <>@{p.from_username} commented: <i>&ldquo;{p.text}&rdquo;</i></>;
    if (n.kind === "tip") return <>@{p.from_username} sent you a <b>${(p.net_usd || 0).toFixed(2)}</b> tip{p.message ? ` — &ldquo;${p.message}&rdquo;` : ""}</>;
    return n.kind;
}

function linkFor(n) {
    const p = n.payload || {};
    if (n.kind === "tip") return "/payouts";
    if (p.post_id) return `/p/${p.post_id}`;
    return "/";
}

export default function NotificationBell() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);
    const [items, setItems] = useState([]);
    const ref = useRef();

    useEffect(() => {
        if (!user) return;
        const load = () => api.get("/notifications/unread-count").then((r) => setCount(r.data.count)).catch(() => {});
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [user]);

    useEffect(() => {
        const close = (e) => {
            if (!ref.current || ref.current.contains(e.target)) return;
            setOpen(false);
        };
        if (open) document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next) {
            const { data } = await api.get("/notifications");
            setItems(data);
            api.post("/notifications/mark-read").then(() => setCount(0)).catch(() => {});
        }
    };

    if (!user) return null;

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={toggle}
                className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg brut-inset bg-white hover:bg-[#FFE55C]"
                data-testid="nav-notifications"
                aria-label="Notifications"
            >
                <Bell className="w-4 h-4" />
                {count > 0 && (
                    <span
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5A5F] border-2 border-[#0A0A0A] text-white text-[10px] font-black flex items-center justify-center"
                        data-testid="notif-count-badge"
                    >
                        {count > 9 ? "9+" : count}
                    </span>
                )}
            </button>
            {open && (
                <div
                    className="absolute right-0 mt-2 w-80 sm:w-96 max-h-96 overflow-y-auto brut-lg bg-white rounded-xl p-3 z-50"
                    data-testid="notif-dropdown"
                >
                    <div className="text-xs font-black uppercase tracking-wider text-[#4A4A4A] px-2 py-1">
                        Notifications
                    </div>
                    {items.length === 0 ? (
                        <div className="text-sm font-medium text-[#4A4A4A] italic px-2 py-3">No notifications yet.</div>
                    ) : (
                        <ul className="space-y-1">
                            {items.map((n) => {
                                const Icon = ICONS[n.kind] || Bell;
                                return (
                                    <li key={n.id}>
                                        <Link
                                            to={linkFor(n)}
                                            onClick={() => setOpen(false)}
                                            className={`flex items-start gap-2 rounded-lg p-2 hover:bg-[#FFFDF7] border-2 ${n.read ? "border-transparent" : "border-[#0A0A0A]/10"}`}
                                            data-testid={`notif-${n.id}`}
                                        >
                                            <div className={`w-8 h-8 rounded-lg border-2 border-[#0A0A0A] flex items-center justify-center shrink-0 ${BG[n.kind] || "bg-white"}`}>
                                                <Icon className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium">{renderText(n)}</div>
                                                <div className="text-[11px] text-[#4A4A4A] mt-0.5">{timeAgo(n.created_at)}</div>
                                            </div>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
